// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { EnvironmentType, IEnvironment } from './environmentType';
import { DevEnvironment } from './environment.dev';
import { IntegrationEnvironment } from './environment.int';
import { LocalEnvironment } from './environment.local';
import { ProductionEnvironment } from './environment.prod';
import { environmentOverride } from './localEnvironmentOverride';

/**
 * The file contents for the current environment will overwrite these during build.
 * The build system defaults to the dev environment which uses `environment.ts`, but if you do
 * `ng build --env=prod` then `environment.prod.ts` will be used instead.
 * The list of which env maps to which file can be found in `.angular-cli.json`.
 */
export class Environment {
    constructor() { }

    /**
     * Get the current environment settings
     */
    static get getEnvironment(): IEnvironment {
        let hostUrl: string = location.host.toUpperCase();

        if (hostUrl.includes('LOCALHOST')) {
            hostUrl = environmentOverride();
        }
        if (hostUrl.includes('DEV')) {
            return DevEnvironment.getConfiguration();
        }
        if (hostUrl.includes('INT') || hostUrl.includes('UAT')) {
            return IntegrationEnvironment.getConfiguration();
        }
        if (hostUrl.includes('ROBO') || hostUrl.includes('PROD')) {
            return ProductionEnvironment.getConfiguration();
        }
        return LocalEnvironment.getConfiguration();
    }

    /**
     * Get a list of all the supported environments
     * @returns All environment configurations as a 'dictionary'
     */
    static get getAllEnvironments(): { [environmentType: string]: IEnvironment } {
        const environments: { [environmentType: string]: IEnvironment } = {};
        environments[EnvironmentType.LocalHost] = LocalEnvironment.getConfiguration();
        environments[EnvironmentType.Integration] = IntegrationEnvironment.getConfiguration();
        environments[EnvironmentType.Production] = ProductionEnvironment.getConfiguration();

        return environments;
    }

    /**
     * Get a list of all the supported environments
     * @returns A list of environments
     */
    static get getEnvironments(): IEnvironment[] {
        return [
            LocalEnvironment.getConfiguration(),
            IntegrationEnvironment.getConfiguration(),
            ProductionEnvironment.getConfiguration(),
        ];
    }
}
